import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Simple Cart Store (No Backend Required)
 * Perfect for MVP - uses localStorage only
 */
const useSimpleCartStore = create(
  persist(
    (set, get) => ({
      // ====================================================================
      // STATE
      // ====================================================================
      items: [],

      // ====================================================================
      // ADD TO CART
      // ====================================================================
      addToCart: (item) => {
        const { items } = get();

        // Check if item already exists (by design id)
        const existingItemIndex = items.findIndex(
          (i) => i.design?.id === item.design?.id
        );

        if (existingItemIndex >= 0) {
          // Update quantity if exists
          const updatedItems = [...items];
          updatedItems[existingItemIndex] = {
            ...updatedItems[existingItemIndex],
            quantity: updatedItems[existingItemIndex].quantity + 1,
          };
          set({ items: updatedItems });
        } else {
          // Add new item
          set({
            items: [
              ...items,
              {
                id: Date.now().toString(), // Simple ID
                ...item,
                quantity: item.quantity || 1,
                addedAt: new Date().toISOString(),
              },
            ],
          });
        }
      },

      // ====================================================================
      // REMOVE FROM CART
      // ====================================================================
      removeFromCart: (itemId) => {
        const { items } = get();
        set({ items: items.filter((item) => item.id !== itemId) });
      },

      // ====================================================================
      // UPDATE QUANTITY
      // ====================================================================
      updateQuantity: (itemId, quantity) => {
        const { items } = get();
        if (quantity < 1) {
          // Remove if quantity is 0
          get().removeFromCart(itemId);
          return;
        }

        const updatedItems = items.map((item) =>
          item.id === itemId ? { ...item, quantity } : item
        );
        set({ items: updatedItems });
      },

      // ====================================================================
      // CLEAR CART
      // ====================================================================
      clearCart: () => {
        set({ items: [] });
      },

      // ====================================================================
      // HELPERS
      // ====================================================================
      getItemCount: () => {
        const { items } = get();
        return items.reduce((count, item) => count + item.quantity, 0);
      },

      getTotal: () => {
        const { items } = get();
        return items.reduce((total, item) => {
          const price = item.price || 2999; // Default $29.99
          return total + price * item.quantity;
        }, 0);
      },

      isInCart: (designId) => {
        const { items } = get();
        return items.some((item) => item.design?.id === designId);
      },
    }),
    {
      name: 'cloudcard-cart', // localStorage key
    }
  )
);

export default useSimpleCartStore;
